/**
 * Entités commerciales : ce qui peut devenir client.
 *
 * Distinction fondatrice de ce module, et il faut l'avoir en tête pour lire le
 * reste du code :
 *
 *   ÉTABLISSEMENT — un lieu sur la carte. GMF ou hôpital. Statique, vient du
 *                   répertoire du MSSS.
 *   ENTITÉ         — ce qui a un statut, un effectif, un revenu. Un GMF est les
 *   COMMERCIALE     deux à la fois ; un hôpital n'est qu'un lieu.
 *
 * Un hôpital ne devient pas client : ses départements le deviennent, chacun
 * pour son compte. On ne vend pas « à l'Hôpital du Sacré-Cœur », on vend à son
 * urgence, puis peut-être à son obstétrique, avec des interlocuteurs, des
 * effectifs et des dates différents.
 *
 * Les départements n'existent que créés. Un hôpital n'a pas 200 départements
 * en attente : il en a zéro jusqu'à ce que quelqu'un en ajoute un. Ils vivent
 * donc dans `records` (kind='dept'), pas dans le jeu de données statique — ce
 * sont des dossiers, et le dossier est l'existence.
 */

import { recordKey } from './record.js'

/**
 * Rang d'avancement d'une relation. Sert à colorer le marqueur d'un hôpital :
 * la relation la plus avancée l'emporte.
 *
 * Un partenaire passe devant une négociation : il utilise le produit, la
 * relation existe. Une négociation n'est encore qu'une conversation.
 */
const RANK = { prospect: 0, conversion: 1, gratuit: 2, client: 3 }

/** Entités commerciales d'un hôpital : ses départements, dans l'ordre de création. */
export function departmentsOf(hospitalId, records) {
  const out = []
  for (const record of records.values()) {
    if (record.kind === 'dept' && record.parentId === hospitalId) out.push(record)
  }
  return out.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'fr-CA'))
}

/**
 * Statut affiché pour un hôpital : celui de sa relation la plus avancée.
 *
 * Sans département, il est prospect — personne ne l'a encore travaillé.
 */
export function hospitalStatus(hospitalId, records) {
  const depts = departmentsOf(hospitalId, records)
  if (depts.length === 0) return 'prospect'
  return depts.reduce(
    (best, d) => (RANK[d.status] > RANK[best] ? d.status : best),
    'prospect',
  )
}

/** Tous les statuts présents dans un hôpital — un hôpital peut être client ET en négociation. */
export function hospitalStatuses(hospitalId, records) {
  const depts = departmentsOf(hospitalId, records)
  if (depts.length === 0) return new Set(['prospect'])
  return new Set(depts.map((d) => d.status))
}

/**
 * Toutes les entités commerciales : les GMF, et les départements créés.
 *
 * Les hôpitaux n'y figurent pas — ils n'ont pas de statut propre. Un hôpital
 * sans département ne compte donc nulle part, et c'est juste : on ne l'a pas
 * encore travaillé, il n'y a rien à compter.
 *
 * Un département porte `pros` mais jamais d'estimation : `kind` valant 'dept',
 * estimatePros() rend 0 — seuls les GMF ont une clientèle inscrite publiée.
 */
export function commercialEntities(establishments, records) {
  const entities = []

  for (const est of establishments) {
    if (est.kind === 'gmf') entities.push(est)
  }

  for (const record of records.values()) {
    if (record.kind !== 'dept') continue
    entities.push({
      kind: 'dept',
      id: record.id,
      parentId: record.parentId,
      nom: record.name,
    })
  }

  return entities
}

/** Le dossier d'une entité, ou undefined. */
export function recordOf(entity, records) {
  return records.get(recordKey(entity.kind, entity.id))
}
